// Admin user management (M7). Lists users, keeps them fresh off the user.changed SSE, and
// creates/edits/deactivates/resets through the offline queue. Mirrors ClinicsPage's
// load / live-refresh / queued-mutate shape. Create and edit share an inline modal; a second
// tiny modal handles password resets. Server business errors (last_super_admin, username_taken)
// surface via ApiError.message.
import { useCallback, useEffect, useState } from 'react';
import {
  type AdminUser,
  type CreateUserRequest,
  type Role,
  createUserSchema,
  updateUserSchema,
  resetPasswordSchema,
  ROLES,
  ROLE_LABELS,
  formatDate,
} from '@alignova/shared';
import { api, ApiError, isDeferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { PasswordInput } from '../../ui/PasswordInput.tsx';
import { Button, Card, PageHeader, Pill } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

function roleLabel(role: string): string {
  return ROLE_LABELS[role as Role] ?? role;
}

// Surface the server's typed business error (last_super_admin / username_taken / …) verbatim,
// falling back to a generic message for anything else.
function messageFor(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function UsersPage() {
  const can = useCan();
  const manage = can('user:manage');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);

  const load = useCallback((search: string) => {
    const q = search.trim();
    const path = q ? `/users?search=${encodeURIComponent(q)}` : '/users';
    api
      .get<AdminUser[]>(path)
      .then(setUsers)
      .catch(() => setError(t('users.loadError')));
  }, []);

  // Mount-only: search reloads are driven by the input below (load is a stable useCallback).
  useEffect(() => {
    load(query);
  }, [load]);

  // A user was created, edited, or (de)activated somewhere — pull fresh truth, keeping the search.
  useLiveEvent('user.changed', () => {
    setQueued(false);
    load(query);
  });

  function openCreate() {
    setEditing(null);
    setShowModal(true);
  }

  function openEdit(user: AdminUser) {
    setEditing(user);
    setShowModal(true);
  }

  // Flip active state through the offline queue; the user.changed refresh reconciles the row.
  async function toggleActive(user: AdminUser) {
    setError(null);
    try {
      const res = user.isActive
        ? await api.post<AdminUser>(`/users/${user.id}/deactivate`, {}, { queue: true })
        : await api.patch<AdminUser>(`/users/${user.id}`, { isActive: true }, { queue: true });
      if (isDeferred(res)) setQueued(true);
    } catch (err) {
      setError(messageFor(err, t('users.toggleError')));
      load(query);
    }
  }

  return (
    <div>
      <PageHeader title={t('users.title')} subtitle={t('users.subtitle')} />

      <div className="mb-4 flex items-center gap-2">
        <form
          className="flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            load(query);
          }}
        >
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              load(e.target.value);
            }}
            placeholder={t('users.searchPlaceholder')}
            aria-label={t('users.searchPlaceholder')}
            className="w-full max-w-sm rounded border border-line bg-surface px-3 py-2 text-sm"
          />
        </form>
        {manage && (
          <Button className="ml-auto" onClick={openCreate}>
            {t('users.new')}
          </Button>
        )}
      </div>

      {queued && (
        <div className="mb-3 rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          {t('sync.queued')}
        </div>
      )}
      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}

      <Card className="overflow-hidden p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">{t('users.col.username')}</th>
              <th className="px-4 py-3 font-medium">{t('users.col.name')}</th>
              <th className="px-4 py-3 font-medium">{t('users.col.role')}</th>
              <th className="px-4 py-3 font-medium">{t('users.col.active')}</th>
              <th className="px-4 py-3 font-medium">{t('users.col.created')}</th>
              {manage && <th className="px-4 py-3 text-right font-medium">{t('users.col.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={manage ? 6 : 5} className="px-4 py-6 text-center text-muted">
                  {t('users.empty')}
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-b border-line hover:bg-surface-2">
                <td className="px-4 py-2 font-mono text-xs">{u.username}</td>
                <td className="px-4 py-2">{u.displayName}</td>
                <td className="px-4 py-2">
                  <Pill tone="neutral">{roleLabel(u.role)}</Pill>
                </td>
                <td className="px-4 py-2">
                  <Pill tone={u.isActive ? 'good' : 'neutral'}>
                    {u.isActive ? t('users.status.active') : t('users.status.inactive')}
                  </Pill>
                </td>
                <td className="px-4 py-2">{formatDate(u.createdAt)}</td>
                {manage && (
                  <td className="px-4 py-2">
                    {/* Compact, right-aligned actions: three full-width buttons a row was what
                        pushed this table off the side of the screen. */}
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>
                        {t('users.edit')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleActive(u)}>
                        {u.isActive ? t('users.deactivate') : t('users.activate')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setResetTarget(u)}>
                        {t('users.resetPassword')}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <UserModal
        open={showModal}
        user={editing}
        onClose={() => setShowModal(false)}
        onSaved={() => {
          setQueued(false);
          load(query);
        }}
        onQueued={() => setQueued(true)}
      />

      <ResetPasswordModal
        user={resetTarget}
        onClose={() => setResetTarget(null)}
        onQueued={() => setQueued(true)}
      />
    </div>
  );
}

// Create / edit modal. A null `user` is create (username + password + role + optional
// clinicId/caseId → createUserSchema → POST /users). A user is edit (role/name/email/active only,
// NO username/password → updateUserSchema → PATCH /users/:id).
function UserModal({
  open,
  user,
  onClose,
  onSaved,
  onQueued,
}: {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
  onSaved: () => void;
  onQueued: () => void;
}) {
  const isEdit = user !== null;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('data_entry');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [caseId, setCaseId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUsername('');
    setPassword('');
    setRole((user?.role as Role) ?? 'data_entry');
    setDisplayName(user?.displayName ?? '');
    setEmail(user?.email ?? '');
    setClinicId('');
    setCaseId('');
    setIsActive(user?.isActive ?? true);
    setError(null);
    setSubmitting(false);
  }, [open, user]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (user) {
        const parsed = updateUserSchema.safeParse({
          role,
          displayName,
          email: email.trim() || null,
          isActive,
        });
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? t('users.form.invalid'));
          setSubmitting(false);
          return;
        }
        const res = await api.patch<AdminUser>(`/users/${user.id}`, parsed.data, { queue: true });
        if (isDeferred(res)) onQueued();
      } else {
        const payload: CreateUserRequest = {
          username,
          password,
          role,
          displayName,
          email: email.trim() || null,
          clinicId: clinicId.trim() || null,
          caseId: caseId.trim() || null,
        };
        const parsed = createUserSchema.safeParse(payload);
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? t('users.form.invalid'));
          setSubmitting(false);
          return;
        }
        const res = await api.post<AdminUser>('/users', parsed.data, { queue: true });
        if (isDeferred(res)) onQueued();
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(messageFor(err, t('users.form.saveError')));
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
      <Card className="max-h-screen w-full max-w-2xl overflow-y-auto p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">
          {isEdit ? t('users.form.editTitle') : t('users.form.createTitle')}
        </h2>
        <form className="space-y-4" onSubmit={submit}>
          {!isEdit && (
            <div className="grid grid-cols-1 gap-x-4 gap-y-3">
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.form.username')}</label>
                <input className={inputClass} value={username} onChange={(e) => setUsername(e.target.value)} required />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.form.password')}</label>
                <PasswordInput
                  className={inputClass}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.form.role')}</label>
              <select className={inputClass} value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.form.displayName')}</label>
              <input
                className={inputClass}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.form.email')}</label>
            <input type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {!isEdit && (
            <div className="grid grid-cols-1 gap-x-4 gap-y-3">
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.form.clinicId')}</label>
                <input className={inputClass} value={clinicId} onChange={(e) => setClinicId(e.target.value)} />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.form.caseId')}</label>
                <input className={inputClass} value={caseId} onChange={(e) => setCaseId(e.target.value)} />
              </div>
            </div>
          )}

          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              {t('users.form.isActive')}
            </label>
          )}

          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('users.form.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('users.form.saving') : t('users.form.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// Tiny reset-password modal: a single new-password field → resetPasswordSchema → POST reset-password.
function ResetPasswordModal({
  user,
  onClose,
  onQueued,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onQueued: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPassword('');
    setError(null);
    setSubmitting(false);
  }, [user]);

  if (!user) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setError(null);
    const parsed = resetPasswordSchema.safeParse({ password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('users.form.invalid'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/users/${user.id}/reset-password`, parsed.data, { queue: true });
      if (isDeferred(res)) onQueued();
      onClose();
    } catch (err) {
      setError(messageFor(err, t('users.reset.saveError')));
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-sm p-6">
        <h2 className="mb-1 text-lg font-semibold text-ink">{t('users.reset.title')}</h2>
        <p className="mb-4 text-sm text-muted">{user.displayName}</p>
        <form className="space-y-4" onSubmit={submit}>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('users.reset.password')}</label>
            <PasswordInput
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('users.form.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('users.reset.saving') : t('users.reset.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
